import { JsonObject } from './jsonObject.js';
import { JsonValue, JsonValueType, toJsonValue } from './jsonValue.js';
import { Logger } from '@arshu/common/logger';

export class JsonArray extends Array<JsonValue> {
    private _isReadOnly = false;

    constructor(capacity: number | null = null) {
        super();
        if (capacity && typeof capacity === 'number') {
            this.length = 0;
        }
    }

    get isReadOnly(): boolean {
        return this._isReadOnly;
    }

    set isReadOnly(value: boolean) {
        this._isReadOnly = value;
        for (const childValue of this) {
            JsonObject.applyReadOnlyToJsonValue(childValue, value);
        }
    }

    add(item: unknown): void {
        this.insertItem(this.length, toJsonValue(item));
    }

    push(...items: unknown[]): number {
        for (const item of items) {
            this.insertItem(this.length, toJsonValue(item));
        }
        return this.length;
    }

    get(index: number): JsonValue {
        const value = this[index];
        return value !== undefined ? value : JsonValue.null();
    }

    set(index: number, item: unknown): void {
        this.setItem(index, toJsonValue(item));
    }

    insertItem(index: number, item: JsonValue): void {
        if (this._isReadOnly) {
            JsonArray.logReadOnlyWriteIgnored('insert item');
            return;
        }
        JsonObject.applyReadOnlyToJsonValue(item, this._isReadOnly);
        super.splice(index, 0, item);
    }

    setItem(index: number, item: JsonValue): void {
        if (this._isReadOnly) {
            JsonArray.logReadOnlyWriteIgnored('set item');
            return;
        }
        JsonObject.applyReadOnlyToJsonValue(item, this._isReadOnly);
        this[index] = item;
    }

    removeItem(index: number): void {
        if (this._isReadOnly) {
            JsonArray.logReadOnlyWriteIgnored('remove item');
            return;
        }
        super.splice(index, 1);
    }

    clearItems(): void {
        if (this._isReadOnly) {
            JsonArray.logReadOnlyWriteIgnored('clear');
            return;
        }
        super.splice(0, this.length);
    }

    addRange(items: Iterable<JsonValue>): void {
        for (const item of items) {
            this.add(item);
        }
    }

    /**
     * Returns a structural deep copy of this array into a fresh mutable JsonArray.
     * Element order is preserved; nested objects/arrays are recursively cloned. No text
     * serialization. Replaces the historical serialize-then-parse clone round-trip.
     */
    deepClone(): JsonArray {
        const clone = new JsonArray(this.length);
        for (let i = 0; i < this.length; i++) {
            clone.add(this[i].deepClone());
        }
        return clone;
    }

    size(): number {
        return this.length;
    }

    get count(): number {
        return this.length;
    }

    isEmpty(): boolean {
        return this.length === 0;
    }

    values(): ArrayIterator<JsonValue> {
        let index = 0;
        const array = this;
        return {
            next(): IteratorResult<JsonValue> {
                if (index >= array.length) {
                    return { done: true, value: undefined };
                }

                const value = array[index];
                index++;
                return { done: false, value };
            },
            [Symbol.dispose](): void {
                // no-op for iterator protocol compatibility
            },
            [Symbol.iterator](): ArrayIterator<JsonValue> {
                return this;
            }
        };
    }

    getEnumerator(): ArrayIterator<JsonValue> {
        return super[Symbol.iterator]();
    }

    [Symbol.iterator](): ArrayIterator<JsonValue> {
        return super[Symbol.iterator]();
    }

    map<U>(callback: (value: JsonValue, index: number, array: JsonValue[]) => U, thisArg?: unknown): U[] {
        const result: U[] = [];
        for (let i = 0; i < this.length; i++) {
            result.push(callback.call(thisArg, this[i], i, this));
        }
        return result;
    }

    forEach(callback: (value: JsonValue, index: number, array: JsonValue[]) => void, thisArg?: unknown): void {
        for (let i = 0; i < this.length; i++) {
            callback.call(thisArg, this[i], i, this);
        }
    }

    private static logReadOnlyWriteIgnored(operation: string): void {
        const message = `JsonArray read-only write ignored: ${operation}`;
        Logger.warn(message, 'JsonArray');
    }

    toArray(): unknown[] {
        const arr: unknown[] = [];
        for (let i = 0; i < this.length; i++) {
            const value = this[i];
            if (value instanceof JsonValue) {
                switch (value.typeName) {
                    case JsonValueType.String:
                        arr.push(value.asString());
                        break;
                    case JsonValueType.Number:
                        arr.push(value.asNumber());
                        break;
                    case JsonValueType.Integer:
                        arr.push(value.asInteger());
                        break;
                    case JsonValueType.Bool:
                        arr.push(value.asBool());
                        break;
                    case JsonValueType.Array: {
                        const innerArr = value.asArray();
                        if (innerArr && typeof innerArr.toArray === 'function') {
                            arr.push(innerArr.toArray());
                        } else if (Array.isArray(innerArr)) {
                            arr.push(innerArr);
                        } else {
                            arr.push([]);
                        }
                        break;
                    }
                    case JsonValueType.Object: {
                        const innerObj = value.asObject();
                        if (innerObj && typeof innerObj.toObject === 'function') {
                            arr.push(innerObj.toObject());
                        } else if (innerObj && typeof innerObj === 'object') {
                            arr.push(innerObj);
                        } else {
                            arr.push({});
                        }
                        break;
                    }
                    case JsonValueType.Null:
                    default:
                        arr.push(null);
                        break;
                }
            } else {
                arr.push(value);
            }
        }
        return arr;
    }
}
